// C:\IntegraNube_WebStore\src\pages\Inicio.js
'use strict';

import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';

import {
  ArrowBackIosNewRounded as ArrowBackIcon,
  ArrowForwardIosRounded as ArrowNextIcon,
  ArrowForwardOutlined as ArrowForwardIcon,
  CategoryOutlined as CategoryIcon,
  LocalOfferOutlined as LocalOfferIcon,
  LocalShippingOutlined as LocalShippingIcon,
  ShoppingBagOutlined as ShoppingBagIcon,
  StorefrontOutlined as StorefrontIcon,
  SupportAgentOutlined as SupportAgentIcon,
} from '@mui/icons-material';

import api from '../api/api';

/* ===================================================================== */
/*  Helpers configuración                                                */
/* ===================================================================== */

function resolveTiendaData(source) {
  if (!source || typeof source !== 'object') return {};

  if (source.status && source.data && typeof source.data === 'object') {
    return source.data;
  }

  if (
    source.data &&
    typeof source.data === 'object' &&
    (source.data.branding || source.data.nombre)
  ) {
    return source.data;
  }

  return source;
}

function safeColor(value, fallback) {
  const color = String(value || '').trim();
  return color || fallback;
}

function buildAddress(direccion = {}) {
  return [
    direccion?.direccion,
    direccion?.comuna,
    direccion?.ciudad,
    direccion?.region,
  ]
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .filter((value, index, arr) => arr.indexOf(value) === index)
    .join(', ');
}

/* ===================================================================== */
/*  Helpers productos                                                    */
/* ===================================================================== */

function extractProductList(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.productos)) {
    return payload.productos;
  }

  if (Array.isArray(payload?.items)) {
    return payload.items;
  }

  if (Array.isArray(payload?.data?.productos)) {
    return payload.data.productos;
  }

  if (Array.isArray(payload?.data?.items)) {
    return payload.data.items;
  }

  return [];
}

function getProductImage(producto) {
  if (!producto || typeof producto !== 'object') {
    return null;
  }

  const directCandidates = [
    producto.imagen,
    producto.imagen_url,
    producto.imagenUrl,
    producto.imagen_principal,
    producto.imagenPrincipal,
    producto.foto,
    producto.foto_url,
    producto.thumbnail,
    producto.thumbnail_url,
  ];

  for (const candidate of directCandidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }

  const collections = [
    producto.imagenes,
    producto.images,
    producto.fotos,
  ];

  for (const collection of collections) {
    if (!Array.isArray(collection) || !collection.length) {
      continue;
    }

    const first = collection[0];

    if (typeof first === 'string' && first.trim()) {
      return first.trim();
    }

    if (first && typeof first === 'object') {
      const value =
        first.url ||
        first.imagen_url ||
        first.src ||
        first.path;

      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
  }

  return null;
}

function formatPrice(
  value,
  moneda = 'CLP',
  locale = 'es-CL'
) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return null;
  }

  try {
    return new Intl.NumberFormat(
      locale || 'es-CL',
      {
        style: 'currency',
        currency:
          moneda || 'CLP',
        maximumFractionDigits:
          moneda === 'CLP'
            ? 0
            : 2,
      }
    ).format(number);
  } catch (_) {
    return `$${Math.round(
      number
    ).toLocaleString(
      locale || 'es-CL'
    )}`;
  }
}

function getOfferInfo(producto) {
  const precioActual = Number(
    producto?.precio ??
    producto?.precio_bruto ??
    producto?.precio_venta ??
    producto?.precioVenta
  );

  const precioNormal = Number(
    producto?.precioNormal ??
    producto?.precio_normal ??
    producto?.precioNormalBruto ??
    producto?.precio_normal_bruto
  );

  const ofertaMarcada =
    producto?.enOferta === true ||
    producto?.oferta?.activa === true;

  const ofertaValida =
    Number.isFinite(precioActual) &&
    precioActual > 0 &&
    Number.isFinite(precioNormal) &&
    precioNormal > precioActual;

  const activa =
    ofertaValida &&
    (ofertaMarcada || precioNormal > precioActual);

  if (!activa) {
    return {
      activa: false,
      precioActual,
      precioNormal: null,
      descuentoPorcentaje: 0,
      ahorro: 0,
    };
  }

  const ahorro =
    Math.max(0, precioNormal - precioActual);

  const descuentoPorcentaje =
    precioNormal > 0
      ? Math.round((ahorro / precioNormal) * 100)
      : 0;

  return {
    activa: true,
    precioActual,
    precioNormal,
    descuentoPorcentaje,
    ahorro,
  };
}

function extractCategories(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  const data =
    payload?.data &&
    typeof payload.data === 'object' &&
    !Array.isArray(payload.data)
      ? payload.data
      : payload;

  return Array.isArray(data?.categorias)
    ? data.categorias
    : [];
}

function getCategoryPath(categoria) {
  const ruta =
    String(categoria?.ruta || '').trim();

  if (
    ruta &&
    ruta.startsWith('/categoria/')
  ) {
    return ruta;
  }

  const slug =
    String(categoria?.slug || '').trim();

  return slug
    ? `/categoria/${encodeURIComponent(slug)}`
    : '/productos';
}

function isFeaturedProduct(producto) {
  const value =
    producto?.destacado ??
    producto?.es_destacado ??
    producto?.featured;

  if (value === true || value === 1 || value === '1') {
    return true;
  }

  if (typeof value === 'string') {
    return value.trim().toLowerCase() === 'true';
  }

  return false;
}

function isAvailableProduct(producto) {
  const value =
    producto?.disponible ??
    producto?.activo ??
    producto?.publicado;

  /*
   * Si el backend no entrega el campo, no descartamos el producto.
   * La consulta ya solicita disponible=true.
   */
  if (value === undefined || value === null) {
    return true;
  }

  if (value === true || value === 1 || value === '1') {
    return true;
  }

  if (typeof value === 'string') {
    return value.trim().toLowerCase() === 'true';
  }

  return false;
}

function getProductPath(producto) {
  const slug =
    String(producto?.slug || '')
      .trim();

  /*
   * URL pública/canónica definitiva del producto.
   */
  return slug
    ? `/producto/${encodeURIComponent(slug)}`
    : '/productos';
}

/* ===================================================================== */
/*  Card destacado                                                       */
/* ===================================================================== */

function FeaturedProductCard({
  producto,
  colorPrimario,
  colorTexto,
  colorTextoSecundario,
  colorOferta,
  colorExito,
  colorSuperficie,
  colorFondo,
  monedaDefault,
  locale,
  mostrarStock,
  mostrarSku,
  mostrarOfertas,
  mostrarPrecioNormalOferta,
  mostrarPorcentajeOferta,
  mostrarAhorroOferta,
}) {
  const nombre =
    String(
      producto?.nombre ||
      producto?.titulo ||
      producto?.name ||
      'Producto'
    ).trim();

  const sku =
    String(
      producto?.sku ||
      producto?.codigo ||
      ''
    ).trim();

  const imageUrl = getProductImage(producto);

  const oferta =
    getOfferInfo(producto);

  const ofertaVisible =
    mostrarOfertas &&
    oferta.activa;

  const monedaProducto =
    producto?.moneda ||
    monedaDefault ||
    'CLP';

  const precio = formatPrice(
    oferta.precioActual,
    monedaProducto,
    locale
  );

  const precioNormal =
    ofertaVisible &&
    mostrarPrecioNormalOferta
      ? formatPrice(
          oferta.precioNormal,
          monedaProducto,
          locale
        )
      : null;

  const stockValue =
    producto?.stock ??
    producto?.stock_disponible ??
    producto?.stockDisponible;

  const stock =
    Number.isFinite(Number(stockValue))
      ? Number(stockValue)
      : null;

  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [imageUrl]);

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        border: '1px solid #e5e7eb',
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: colorSuperficie,
        transition:
          'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          borderColor: '#d1d5db',
          boxShadow: '0 16px 36px rgba(15,23,42,0.08)',
        },
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={getProductPath(producto)}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '1 / 1',
            bgcolor: colorFondo,
            overflow: 'hidden',
          }}
        >
          {imageUrl && !imageError ? (
            <CardMedia
              component="img"
              image={imageUrl}
              alt={nombre}
              loading="lazy"
              onError={() => setImageError(true)}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                p: 1.5,
              }}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'grid',
                placeItems: 'center',
                color: '#9ca3af',
              }}
            >
              <StorefrontIcon
                sx={{
                  fontSize: 56,
                  opacity: 0.35,
                }}
              />
            </Box>
          )}

          <Chip
            label="Destacado"
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              bgcolor: '#fff',
              color: colorPrimario,
              fontWeight: 800,
              border: `1px solid ${colorPrimario}30`,
              boxShadow: '0 4px 12px rgba(15,23,42,0.06)',
            }}
          />

          {ofertaVisible ? (
            <Chip
              icon={
                <LocalOfferIcon
                  sx={{
                    fontSize:
                      '15px !important',
                  }}
                />
              }
              label={
                mostrarPorcentajeOferta
                  ? `${oferta.descuentoPorcentaje}% OFF`
                  : 'Oferta'
              }
              size="small"
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                bgcolor: colorOferta,
                color: '#fff',
                fontWeight: 900,
                '& .MuiChip-icon': {
                  color: '#fff',
                },
              }}
            />
          ) : null}
        </Box>

        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            width: '100%',
            p: 2,
          }}
        >
          {mostrarSku && sku ? (
            <Typography
              variant="caption"
              sx={{
                mb: 0.6,
                color:
                  colorTextoSecundario,
                opacity: 0.72,
                fontWeight: 700,
              }}
            >
              SKU {sku}
            </Typography>
          ) : null}

          <Typography
            sx={{
              color: colorTexto,
              fontWeight: 800,
              lineHeight: 1.35,
              minHeight: 44,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {nombre}
          </Typography>

          <Box sx={{ flex: 1 }} />

          {precio ? (
            <Box sx={{ mt: 1.5 }}>
              {ofertaVisible &&
              (precioNormal || mostrarAhorroOferta) ? (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mb: 0.35 }}
                >
                  {precioNormal ? (
                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          colorTextoSecundario,
                        opacity: 0.72,
                        textDecoration:
                          'line-through',
                        fontWeight: 700,
                      }}
                    >
                      {precioNormal}
                    </Typography>
                  ) : null}

                  {mostrarAhorroOferta ? (
                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          colorOferta,
                        fontWeight: 900,
                      }}
                    >
                      Ahorras{' '}
                      {formatPrice(
                        oferta.ahorro,
                        monedaProducto,
                        locale
                      )}
                    </Typography>
                  ) : null}
                </Stack>
              ) : null}

              <Typography
                sx={{
                  color: colorPrimario,
                  fontSize: {
                    xs: 20,
                    sm: 21,
                  },
                  lineHeight: 1.2,
                  fontWeight: 900,
                }}
              >
                {precio}
              </Typography>
            </Box>
          ) : null}

          {mostrarStock && stock !== null ? (
            <Typography
              variant="caption"
              sx={{
                mt: 0.8,
                color:
                  stock > 0
                    ? colorExito
                    : colorTextoSecundario,
                fontWeight: 700,
              }}
            >
              {stock > 0
                ? `${stock.toLocaleString(locale || 'es-CL')} disponible${stock === 1 ? '' : 's'}`
                : 'Sin stock'}
            </Typography>
          ) : null}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

/* ===================================================================== */
/*  Inicio                                                               */
/* ===================================================================== */

export default function Inicio({
  tienda = null,
  config = null,
  storeConfig = null,
}) {
  const source = tienda || config || storeConfig || {};

  const data = useMemo(
    () => resolveTiendaData(source),
    [source]
  );

  const nombre =
    String(data?.nombre || 'Tienda Online').trim();

  const logoUrl =
    String(data?.branding?.logo || '').trim();

  const colorPrimario = safeColor(
    data?.branding?.colorPrimario,
    '#1976d2'
  );

  const colorSecundario = safeColor(
    data?.branding?.colorSecundario,
    '#111827'
  );

  const colorFondo = safeColor(
    data?.branding?.colorFondo,
    '#f8fafc'
  );

  const colorSuperficie = safeColor(
    data?.branding?.colorSuperficie,
    '#ffffff'
  );

  const colorTexto = safeColor(
    data?.branding?.colorTexto,
    '#111827'
  );

  const colorTextoSecundario = safeColor(
    data?.branding?.colorTextoSecundario,
    '#6b7280'
  );

  const colorOferta = safeColor(
    data?.branding?.colorOferta,
    '#dc2626'
  );

  const colorExito = safeColor(
    data?.branding?.colorExito,
    '#059669'
  );

  const moneda =
    String(
      data?.regionalizacion?.moneda ||
      'CLP'
    )
      .trim()
      .toUpperCase();

  const locale =
    String(
      data?.regionalizacion?.locale ||
      'es-CL'
    ).trim();

  const heroTitulo =
    String(
      data?.inicio?.titulo ||
      `Compra online en ${nombre}`
    ).trim();

  const heroSubtitulo =
    String(
      data?.inicio?.subtitulo ||
      'Encuentra productos disponibles, revisa nuestras ofertas y compra directamente desde la tienda.'
    ).trim();

  const mostrarCategoriasInicio =
    data?.inicio?.mostrarCategorias !== false;

  const mostrarDestacadosInicio =
    data?.inicio?.mostrarDestacados !== false;

  const mostrarOfertasInicio =
    data?.inicio?.mostrarOfertas !== false;

  const maxDestacadosInicio =
    Math.min(
      24,
      Math.max(
        1,
        Number(
          data?.inicio?.maxDestacados
        ) || 8
      )
    );

  const mostrarPrecioNormalOferta =
    data?.ofertas?.mostrarPrecioNormal !== false;

  const mostrarPorcentajeOferta =
    data?.ofertas?.mostrarPorcentaje !== false;

  const mostrarAhorroOferta =
    data?.ofertas?.mostrarAhorro !== false;

  const direccion = buildAddress(
    data?.direccion
  );

  const [logoError, setLogoError] =
    useState(false);

  const [destacados, setDestacados] =
    useState([]);

  const [loadingDestacados, setLoadingDestacados] =
    useState(true);

  const [heroIndex, setHeroIndex] =
    useState(0);

  const [heroPaused, setHeroPaused] =
    useState(false);

  const [categorias, setCategorias] =
    useState([]);

  const [loadingCategorias, setLoadingCategorias] =
    useState(true);

  useEffect(() => {
    setLogoError(false);
  }, [logoUrl]);

  /* =================================================================== */
  /*  Productos destacados                                              */
  /* =================================================================== */

  useEffect(() => {
    if (!mostrarDestacadosInicio) {
      setDestacados([]);
      setLoadingDestacados(false);
      return undefined;
    }

    let mounted = true;
    const controller = new AbortController();

    async function cargarDestacados() {
      setLoadingDestacados(true);

      try {
        /*
         * El backend debería filtrar destacado=true.
         *
         * De todas formas, abajo volvemos a validar destacado para no
         * mostrar productos normales si alguna versión antigua del backend
         * ignora ese parámetro.
         */
        const response = await api.get(
          '/productos',
          {
            params: {
              page: 1,
              limit: maxDestacadosInicio,
              destacado: true,
              disponible: true,
            },
            signal: controller.signal,
          }
        );

        const productos = extractProductList(
          response?.data
        );

        const soloDestacados = productos
          .filter(isFeaturedProduct)
          .filter(isAvailableProduct)
          .slice(0, maxDestacadosInicio);

        if (mounted) {
          setDestacados(soloDestacados);
        }
      } catch (error) {
        /*
         * La portada no debe fallar porque la sección de destacados
         * no esté disponible.
         */
        if (
          error?.name !== 'CanceledError' &&
          error?.name !== 'AbortError' &&
          error?.code !== 'ERR_CANCELED'
        ) {
          console.warn(
            '[Inicio] No fue posible cargar productos destacados:',
            error?.message || error
          );
        }

        if (mounted) {
          setDestacados([]);
        }
      } finally {
        if (mounted) {
          setLoadingDestacados(false);
        }
      }
    }

    cargarDestacados();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [
    maxDestacadosInicio,
    mostrarDestacadosInicio,
  ]);

  /* =================================================================== */
  /*  Categorías para navegación rápida                                 */
  /* =================================================================== */

  useEffect(() => {
    if (!mostrarCategoriasInicio) {
      setCategorias([]);
      setLoadingCategorias(false);
      return undefined;
    }

    let mounted = true;
    const controller = new AbortController();

    async function cargarCategorias() {
      setLoadingCategorias(true);

      try {
        const response = await api.get(
          '/categorias',
          {
            signal: controller.signal,
          }
        );

        const items = extractCategories(
          response?.data
        )
          .filter(
            (categoria) =>
              categoria?.id !== undefined &&
              categoria?.id !== null &&
              String(categoria?.nombre || '').trim() &&
              String(categoria?.slug || '').trim()
          )
          .slice(0, 8);

        if (mounted) {
          setCategorias(items);
        }
      } catch (error) {
        if (
          error?.name !== 'CanceledError' &&
          error?.name !== 'AbortError' &&
          error?.code !== 'ERR_CANCELED'
        ) {
          console.warn(
            '[Inicio] No fue posible cargar categorías:',
            error?.message || error
          );
        }

        if (mounted) {
          setCategorias([]);
        }
      } finally {
        if (mounted) {
          setLoadingCategorias(false);
        }
      }
    }

    cargarCategorias();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [
    mostrarCategoriasInicio,
  ]);

  const mostrarLogo =
    Boolean(logoUrl) && !logoError;

  const retiroDisponible =
    data?.ecommerce?.retiroDisponible !== false;

  const despachoDisponible =
    data?.ecommerce?.despachoDisponible === true;

  const contactoHabilitado =
    data?.funciones?.contacto !== false;

  const mostrarStock =
    data?.ecommerce?.mostrarStock !== false;

  const mostrarSku =
    data?.ecommerce?.mostrarSku !== false;

  /*
   * Slider Hero:
   * usamos los mismos productos destacados ya cargados desde /productos.
   * Se priorizan productos con imagen para que cada slide tenga presencia
   * visual; si ninguno tiene imagen, se usan igualmente los destacados.
   */
  const heroProductos =
    useMemo(
      () => {
        const conImagen =
          destacados.filter(
            (producto) =>
              Boolean(
                getProductImage(producto)
              )
          );

        return conImagen.length > 0
          ? conImagen
          : destacados;
      },
      [destacados]
    );

  /*
   * Cuando cambia la colección de destacados, volvemos al primer slide.
   */
  useEffect(() => {
    setHeroIndex(0);
  }, [heroProductos.length]);

  /*
   * Avance automático.
   * Se detiene mientras el usuario mantiene el mouse sobre el Hero,
   * y también cuando sólo existe un producto destacado.
   */
  useEffect(() => {
    if (
      heroPaused ||
      heroProductos.length <= 1
    ) {
      return undefined;
    }

    const timer =
      window.setInterval(
        () => {
          setHeroIndex(
            (current) =>
              (current + 1) %
              heroProductos.length
          );
        },
        6000
      );

    return () => {
      window.clearInterval(timer);
    };
  }, [
    heroPaused,
    heroProductos.length,
  ]);

  const heroProducto =
    heroProductos[heroIndex] ||
    heroProductos[0] ||
    null;

  const irHeroAnterior = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    if (heroProductos.length <= 1) {
      return;
    }

    setHeroIndex(
      (current) =>
        (
          current -
          1 +
          heroProductos.length
        ) %
        heroProductos.length
    );
  };

  const irHeroSiguiente = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    if (heroProductos.length <= 1) {
      return;
    }

    setHeroIndex(
      (current) =>
        (current + 1) %
        heroProductos.length
    );
  };

  const seleccionarHero = (
    event,
    index
  ) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    setHeroIndex(index);
  };

  const heroOferta =
    heroProducto
      ? getOfferInfo(heroProducto)
      : {
          activa: false,
          precioActual: null,
          precioNormal: null,
          descuentoPorcentaje: 0,
          ahorro: 0,
        };

  const heroImagen =
    heroProducto
      ? getProductImage(heroProducto)
      : null;

  const heroOfertaVisible =
    mostrarOfertasInicio &&
    heroOferta.activa;

  const heroMoneda =
    heroProducto?.moneda ||
    moneda;

  const heroPrecio =
    heroProducto
      ? formatPrice(
          heroOferta.precioActual,
          heroMoneda,
          locale
        )
      : null;

  const heroPrecioNormal =
    heroProducto &&
    heroOfertaVisible &&
    mostrarPrecioNormalOferta
      ? formatPrice(
          heroOferta.precioNormal,
          heroMoneda,
          locale
        )
      : null;

  const hayOfertasDestacadas =
    mostrarOfertasInicio &&
    destacados.some(
      (producto) =>
        getOfferInfo(producto).activa
    );

  return (
    <Box
      sx={{
        bgcolor: colorFondo,
        color: colorTexto,
      }}
    >
      {/* =============================================================== */}
      {/* Hero retail                                                     */}
      {/* =============================================================== */}

      <Box
        component="section"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background: `
            radial-gradient(circle at 12% 18%, ${colorPrimario}20 0, transparent 30%),
            radial-gradient(circle at 92% 14%, ${colorSecundario}12 0, transparent 28%),
            linear-gradient(180deg, ${colorSuperficie} 0%, ${colorFondo} 100%)
          `,
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            py: {
              xs: 4,
              sm: 5,
              md: 6,
            },
          }}
        >
          <Grid
            container
            spacing={{
              xs: 3.5,
              md: 6,
            }}
            alignItems="center"
          >
            <Grid item xs={12} md={6}>
              <Stack
                alignItems="flex-start"
              >
                <Chip
                  icon={<ShoppingBagIcon />}
                  label="Tienda online"
                  size="small"
                  sx={{
                    bgcolor: `${colorPrimario}12`,
                    color: colorPrimario,
                    fontWeight: 900,
                    border: `1px solid ${colorPrimario}24`,
                    '& .MuiChip-icon': {
                      color: colorPrimario,
                    },
                  }}
                />

                <Box>
                  <Typography
                    component={mostrarLogo ? 'h1' : 'h2'}
                    sx={{
                      maxWidth: 680,
                      fontSize: {
                        xs: 32,
                        sm: 40,
                        md: 50,
                      },
                      lineHeight: 1.02,
                      letterSpacing: '-0.045em',
                      fontWeight: 950,
                      color: colorTexto,
                    }}
                  >
                    {heroTitulo}
                  </Typography>

                  <Typography
                    sx={{
                      mt: 1.6,
                      maxWidth: 610,
                      color: colorTextoSecundario,
                      fontSize: {
                        xs: 16,
                        md: 18,
                      },
                      lineHeight: 1.65,
                    }}
                  >
                    {heroSubtitulo}
                  </Typography>
                </Box>

                <Stack
                  direction={{
                    xs: 'column',
                    sm: 'row',
                  }}
                  spacing={1.25}
                  sx={{
                    width: {
                      xs: '100%',
                      sm: 'auto',
                    },
                  }}
                >
                  <Button
                    component={RouterLink}
                    to="/productos"
                    variant="contained"
                    startIcon={<ShoppingBagIcon />}
                    sx={{
                      bgcolor: colorPrimario,
                      px: 3,
                      py: 1.15,
                      minHeight: 48,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 900,
                      '&:hover': {
                        bgcolor: colorPrimario,
                        filter: 'brightness(0.92)',
                      },
                    }}
                  >
                    Comprar ahora
                  </Button>

                  {mostrarCategoriasInicio ? (
                    <Button
                      component={RouterLink}
                      to="/categorias"
                      variant="outlined"
                      startIcon={<CategoryIcon />}
                      sx={{
                        borderColor: `${colorPrimario}55`,
                        color: colorPrimario,
                        px: 2.5,
                        py: 1.15,
                        minHeight: 48,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 900,
                        bgcolor: colorSuperficie,
                        '&:hover': {
                          borderColor: colorPrimario,
                          color: colorPrimario,
                          bgcolor: `${colorPrimario}08`,
                        },
                      }}
                    >
                      Ver categorías
                    </Button>
                  ) : null}

                  {contactoHabilitado ? (
                    <Button
                      component={RouterLink}
                      to="/contacto"
                      variant="outlined"
                      startIcon={<SupportAgentIcon />}
                      sx={{
                        borderColor: '#d1d5db',
                        color: '#374151',
                        px: 2.5,
                        py: 1.15,
                        minHeight: 48,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 800,
                        bgcolor: colorSuperficie,
                        '&:hover': {
                          borderColor: colorPrimario,
                          color: colorPrimario,
                          bgcolor: colorSuperficie,
                        },
                      }}
                    >
                      Contacto
                    </Button>
                  ) : null}
                </Stack>
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              {loadingDestacados ? (
                <Skeleton
                  variant="rounded"
                  sx={{
                    width: '100%',
                    minHeight: {
                      xs: 310,
                      md: 390,
                    },
                    borderRadius: 4,
                  }}
                />
              ) : heroProducto ? (
                <Paper
                  component={RouterLink}
                  to={getProductPath(heroProducto)}
                  elevation={0}
                  onMouseEnter={() =>
                    setHeroPaused(true)
                  }
                  onMouseLeave={() =>
                    setHeroPaused(false)
                  }
                  sx={{
                    display: 'block',
                    position: 'relative',
                    overflow: 'hidden',
                    textDecoration: 'none',
                    color: 'inherit',
                    borderRadius: 4,
                    border: '1px solid #e5e7eb',
                    bgcolor: colorSuperficie,
                    boxShadow: '0 24px 70px rgba(15,23,42,0.09)',
                    transition:
                      'transform 180ms ease, box-shadow 180ms ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 30px 80px rgba(15,23,42,0.13)',
                    },
                  }}
                >
                  <Grid
                    container
                    key={
                      heroProducto?.id ??
                      heroProducto?.slug ??
                      heroIndex
                    }
                    sx={{
                      animation:
                        'webstoreHeroFade 320ms ease',
                      '@keyframes webstoreHeroFade': {
                        from: {
                          opacity: 0.35,
                          transform:
                            'translateX(10px)',
                        },
                        to: {
                          opacity: 1,
                          transform:
                            'translateX(0)',
                        },
                      },
                      '@media (prefers-reduced-motion: reduce)': {
                        animation: 'none',
                      },
                    }}
                  >
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          position: 'relative',
                          minHeight: {
                            xs: 230,
                            sm: 330,
                            md: 390,
                          },
                          height: '100%',
                          bgcolor: '#f8fafc',
                          display: 'grid',
                          placeItems: 'center',
                          overflow: 'hidden',
                        }}
                      >
                        {heroImagen ? (
                          <Box
                            component="img"
                            src={heroImagen}
                            alt={
                              heroProducto?.nombre ||
                              'Producto destacado'
                            }
                            loading="eager"
                            decoding="async"
                            sx={{
                              width: '100%',
                              height: '100%',
                              maxHeight: 390,
                              objectFit: 'contain',
                              p: {
                                xs: 2,
                                md: 2.5,
                              },
                            }}
                          />
                        ) : (
                          <StorefrontIcon
                            sx={{
                              fontSize: 74,
                              color: '#cbd5e1',
                            }}
                          />
                        )}

                        {heroOfertaVisible ? (
                          <Chip
                            icon={<LocalOfferIcon />}
                            label={
                              mostrarPorcentajeOferta
                                ? `${heroOferta.descuentoPorcentaje}% OFF`
                                : 'Oferta'
                            }
                            sx={{
                              position: 'absolute',
                              top: 16,
                              left: 16,
                              bgcolor: colorOferta,
                              color: '#fff',
                              fontWeight: 900,
                              '& .MuiChip-icon': {
                                color: '#fff',
                              },
                            }}
                          />
                        ) : (
                          <Chip
                            label="Destacado"
                            sx={{
                              position: 'absolute',
                              top: 16,
                              left: 16,
                              bgcolor: colorSuperficie,
                              color: colorPrimario,
                              fontWeight: 900,
                              border: `1px solid ${colorPrimario}30`,
                            }}
                          />
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Stack
                        sx={{
                          height: '100%',
                          p: {
                            xs: 2.5,
                            md: 3.25,
                          },
                        }}
                        justifyContent="center"
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: colorPrimario,
                            fontWeight: 900,
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                          }}
                        >
                          {heroOfertaVisible
                            ? 'Oferta destacada'
                            : 'Producto destacado'}
                        </Typography>

                        <Typography
                          sx={{
                            mt: 1,
                            color: colorTexto,
                            fontWeight: 900,
                            fontSize: {
                              xs: 20,
                              md: 24,
                            },
                            lineHeight: 1.25,
                          }}
                        >
                          {heroProducto?.nombre || 'Producto'}
                        </Typography>

                        {heroPrecioNormal ? (
                          <Typography
                            sx={{
                              mt: 2,
                              color:
                                colorTextoSecundario,
                              opacity: 0.72,
                              textDecoration: 'line-through',
                              fontWeight: 700,
                            }}
                          >
                            {heroPrecioNormal}
                          </Typography>
                        ) : null}

                        {heroPrecio ? (
                          <Typography
                            sx={{
                              mt: heroPrecioNormal ? 0.2 : 2,
                              color: colorPrimario,
                              fontSize: {
                                xs: 27,
                                md: 32,
                              },
                              fontWeight: 950,
                              lineHeight: 1.1,
                            }}
                          >
                            {heroPrecio}
                          </Typography>
                        ) : null}

                        {heroOfertaVisible &&
                        mostrarAhorroOferta ? (
                          <Typography
                            variant="body2"
                            sx={{
                              mt: 0.8,
                              color:
                                colorOferta,
                              fontWeight: 800,
                            }}
                          >
                            Ahorras{' '}
                            {formatPrice(
                              heroOferta.ahorro,
                              heroMoneda,
                              locale
                            )}
                          </Typography>
                        ) : null}

                        <Button
                          component="span"
                          endIcon={<ArrowForwardIcon />}
                          sx={{
                            mt: 2.5,
                            alignSelf: 'flex-start',
                            px: 0,
                            minWidth: 0,
                            color: colorPrimario,
                            textTransform: 'none',
                            fontWeight: 900,
                            '&:hover': {
                              bgcolor: 'transparent',
                            },
                          }}
                        >
                          Ver producto
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>

                  {heroProductos.length > 1 ? (
                    <>
                      <IconButton
                        type="button"
                        aria-label="Producto destacado anterior"
                        onClick={irHeroAnterior}
                        sx={{
                          position: 'absolute',
                          left: {
                            xs: 10,
                            sm: 12,
                          },
                          top: '50%',
                          transform:
                            'translateY(-50%)',
                          width: 42,
                          height: 42,
                          bgcolor:
                            'rgba(255,255,255,0.92)',
                          color: colorTexto,
                          border:
                            '1px solid rgba(15,23,42,0.10)',
                          boxShadow:
                            '0 6px 18px rgba(15,23,42,0.10)',
                          zIndex: 3,
                          '&:hover': {
                            bgcolor:
                              colorSuperficie,
                            color:
                              colorPrimario,
                          },
                        }}
                      >
                        <ArrowBackIcon
                          sx={{ fontSize: 17 }}
                        />
                      </IconButton>

                      <IconButton
                        type="button"
                        aria-label="Siguiente producto destacado"
                        onClick={irHeroSiguiente}
                        sx={{
                          position: 'absolute',
                          right: {
                            xs: 10,
                            sm: 12,
                          },
                          top: '50%',
                          transform:
                            'translateY(-50%)',
                          width: 42,
                          height: 42,
                          bgcolor:
                            'rgba(255,255,255,0.92)',
                          color: colorTexto,
                          border:
                            '1px solid rgba(15,23,42,0.10)',
                          boxShadow:
                            '0 6px 18px rgba(15,23,42,0.10)',
                          zIndex: 3,
                          '&:hover': {
                            bgcolor:
                              colorSuperficie,
                            color:
                              colorPrimario,
                          },
                        }}
                      >
                        <ArrowNextIcon
                          sx={{ fontSize: 17 }}
                        />
                      </IconButton>

                      <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        justifyContent="center"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                        sx={{
                          position: 'absolute',
                          left: '50%',
                          bottom: 14,
                          transform:
                            'translateX(-50%)',
                          zIndex: 4,
                          px: 1.1,
                          py: 0.65,
                          borderRadius: 999,
                          bgcolor:
                            'rgba(255,255,255,0.88)',
                          border:
                            '1px solid rgba(15,23,42,0.08)',
                          backdropFilter:
                            'blur(8px)',
                        }}
                      >
                        {heroProductos.map(
                          (producto, index) => (
                            <Box
                              key={
                                producto?.id ??
                                producto?.slug ??
                                index
                              }
                              component="button"
                              type="button"
                              aria-label={`Mostrar producto destacado ${index + 1}`}
                              aria-current={
                                index === heroIndex
                                  ? 'true'
                                  : undefined
                              }
                              onClick={(event) =>
                                seleccionarHero(
                                  event,
                                  index
                                )
                              }
                              sx={{
                                appearance:
                                  'none',
                                p: 0,
                                border: 0,
                                width:
                                  index ===
                                  heroIndex
                                    ? 22
                                    : 8,
                                height: 8,
                                borderRadius:
                                  999,
                                bgcolor:
                                  index ===
                                  heroIndex
                                    ? colorPrimario
                                    : '#cbd5e1',
                                cursor:
                                  'pointer',
                                transition:
                                  'width 180ms ease, background-color 180ms ease',
                                '@media (prefers-reduced-motion: reduce)': {
                                  transition:
                                    'none',
                                },
                              }}
                            />
                          )
                        )}
                      </Stack>
                    </>
                  ) : null}
                </Paper>
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    p: {
                      xs: 2.5,
                      sm: 3.5,
                    },
                    borderRadius: 4,
                    border: '1px solid #e5e7eb',
                    bgcolor: colorSuperficie,
                    boxShadow: '0 24px 70px rgba(15,23,42,0.08)',
                  }}
                >
                  <Stack
                    spacing={2}
                    alignItems="flex-start"
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: 3,
                        bgcolor: `${colorPrimario}12`,
                        color: colorPrimario,
                      }}
                    >
                      <ShoppingBagIcon />
                    </Box>

                    <Typography
                      sx={{
                        fontSize: 24,
                        fontWeight: 900,
                        color: colorTexto,
                      }}
                    >
                      Explora nuestro catálogo
                    </Typography>

                    <Typography
                      sx={{
                        color: colorTextoSecundario,
                        lineHeight: 1.65,
                      }}
                    >
                      Revisa los productos disponibles y encuentra lo que
                      necesitas.
                    </Typography>

                    <Button
                      component={RouterLink}
                      to="/productos"
                      variant="contained"
                      sx={{
                        bgcolor: colorPrimario,
                        textTransform: 'none',
                        fontWeight: 900,
                      }}
                    >
                      Ver productos
                    </Button>
                  </Stack>
                </Paper>
              )}
            </Grid>
          </Grid>
        </Container>

        <Box
          sx={{
            borderTop: '1px solid #e5e7eb',
            bgcolor: 'rgba(255,255,255,0.82)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Container
            maxWidth="xl"
            sx={{
              py: 2,
            }}
          >
            <Grid
              container
              spacing={1.5}
              alignItems="stretch"
            >
              <Grid
                item
                xs={12}
                sm={retiroDisponible && despachoDisponible ? 4 : 6}
              >
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: `${colorPrimario}10`,
                      color: colorPrimario,
                      flexShrink: 0,
                    }}
                  >
                    <ShoppingBagIcon fontSize="small" />
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 900,
                        color: colorTexto,
                        fontSize: 14,
                      }}
                    >
                      Compra online
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: colorTextoSecundario }}
                    >
                      Catálogo y precios actualizados
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              {retiroDisponible ? (
                <Grid
                  item
                  xs={12}
                  sm={despachoDisponible ? 4 : 6}
                >
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: `${colorPrimario}10`,
                        color: colorPrimario,
                        flexShrink: 0,
                      }}
                    >
                      <StorefrontIcon fontSize="small" />
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 900,
                          color: colorTexto,
                          fontSize: 14,
                        }}
                      >
                        Retiro disponible
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: colorTextoSecundario }}
                      >
                        {direccion ||
                          'Retira según las condiciones de la tienda'}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              ) : null}

              {despachoDisponible ? (
                <Grid
                  item
                  xs={12}
                  sm={4}
                >
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: `${colorPrimario}10`,
                        color: colorPrimario,
                        flexShrink: 0,
                      }}
                    >
                      <LocalShippingIcon fontSize="small" />
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 900,
                          color: colorTexto,
                          fontSize: 14,
                        }}
                      >
                        Despacho disponible
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: colorTextoSecundario }}
                      >
                        Revisa las opciones para tu compra
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              ) : null}

              {!retiroDisponible &&
              !despachoDisponible &&
              contactoHabilitado ? (
                <Grid item xs={12} sm={6}>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: `${colorPrimario}10`,
                        color: colorPrimario,
                        flexShrink: 0,
                      }}
                    >
                      <SupportAgentIcon fontSize="small" />
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 900,
                          color: colorTexto,
                          fontSize: 14,
                        }}
                      >
                        Atención directa
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: colorTextoSecundario }}
                      >
                        Contáctanos si necesitas ayuda
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              ) : null}
            </Grid>
          </Container>
        </Box>
      </Box>

      {/* =============================================================== */}
      {/* Compra por categoría                                            */}
      {/* =============================================================== */}

      {mostrarCategoriasInicio &&
      (loadingCategorias ? (
        <Box
          component="section"
          sx={{
            bgcolor: colorSuperficie,
            borderBottom: '1px solid #f1f5f9',
          }}
        >
          <Container
            maxWidth="xl"
            sx={{
              py: {
                xs: 4,
                md: 5,
              },
            }}
          >
            <Skeleton
              width={220}
              height={38}
              sx={{ mb: 2 }}
            />

            <Grid container spacing={1.5}>
              {[0, 1, 2, 3].map((item) => (
                <Grid
                  item
                  xs={6}
                  sm={3}
                  key={item}
                >
                  <Skeleton
                    variant="rounded"
                    height={92}
                    sx={{ borderRadius: 3 }}
                  />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      ) : categorias.length > 0 ? (
        <Box
          component="section"
          sx={{
            bgcolor: colorSuperficie,
            borderBottom: '1px solid #f1f5f9',
          }}
        >
          <Container
            maxWidth="xl"
            sx={{
              py: {
                xs: 4,
                md: 5,
              },
            }}
          >
            <Stack
              direction={{
                xs: 'column',
                sm: 'row',
              }}
              spacing={1.5}
              alignItems={{
                xs: 'flex-start',
                sm: 'flex-end',
              }}
              justifyContent="space-between"
              sx={{ mb: 2.5 }}
            >
              <Box>
                <Typography
                  component="h2"
                  sx={{
                    color: colorTexto,
                    fontSize: {
                      xs: 25,
                      md: 30,
                    },
                    fontWeight: 900,
                    letterSpacing: '-0.035em',
                  }}
                >
                  Compra por categoría
                </Typography>

                <Typography
                  sx={{
                    mt: 0.5,
                    color: colorTextoSecundario,
                  }}
                >
                  Encuentra más rápido lo que estás buscando.
                </Typography>
              </Box>

              <Button
                component={RouterLink}
                to="/categorias"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  color: colorPrimario,
                  textTransform: 'none',
                  fontWeight: 900,
                }}
              >
                Ver todas las categorías
              </Button>
            </Stack>

            <Grid container spacing={1.5}>
              {categorias.map((categoria) => (
                <Grid
                  item
                  xs={6}
                  sm={4}
                  md={3}
                  key={categoria.id}
                >
                  <Paper
                    component={RouterLink}
                    to={getCategoryPath(categoria)}
                    elevation={0}
                    sx={{
                      height: '100%',
                      p: {
                        xs: 1.6,
                        sm: 2,
                      },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.4,
                      borderRadius: 3,
                      border: '1px solid #e5e7eb',
                      bgcolor: colorSuperficie,
                      color: 'inherit',
                      textDecoration: 'none',
                      transition:
                        'border-color 160ms ease, transform 160ms ease, box-shadow 160ms ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        borderColor: colorPrimario,
                        boxShadow:
                          '0 10px 24px rgba(15,23,42,0.07)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        flexShrink: 0,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: 2,
                        bgcolor: `${colorPrimario}10`,
                        color: colorPrimario,
                      }}
                    >
                      <CategoryIcon fontSize="small" />
                    </Box>

                    <Box
                      sx={{
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          color: colorTexto,
                          fontWeight: 900,
                          lineHeight: 1.25,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {categoria.nombre}
                      </Typography>

                      {Number(categoria?.productos || 0) > 0 ? (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 0.25,
                            color: '#9ca3af',
                            fontWeight: 700,
                          }}
                        >
                          {Number(
                            categoria.productos
                          ).toLocaleString(locale || 'es-CL')}{' '}
                          producto
                          {Number(categoria.productos) === 1
                            ? ''
                            : 's'}
                        </Typography>
                      ) : null}
                    </Box>

                    <ArrowForwardIcon
                      sx={{
                        color: '#cbd5e1',
                        fontSize: 19,
                        flexShrink: 0,
                      }}
                    />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      ) : null)}

      {/* =============================================================== */}
      {/* Productos destacados                                           */}
      {/* =============================================================== */}

      {mostrarDestacadosInicio &&
      (loadingDestacados ? (
        <Box
          component="section"
          sx={{
            bgcolor: colorSuperficie,
            borderBottom: '1px solid #f1f5f9',
          }}
        >
          <Container
            maxWidth="xl"
            sx={{
              py: {
                xs: 5,
                md: 7,
              },
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 3 }}
            >
              <Box>
                <Skeleton width={210} height={42} />
                <Skeleton width={300} />
              </Box>
            </Stack>

            <Grid container spacing={2.5}>
              {[0, 1, 2, 3].map((item) => (
                <Grid
                  item
                  xs={6}
                  sm={6}
                  md={3}
                  key={item}
                >
                  <Skeleton
                    variant="rounded"
                    sx={{
                      width: '100%',
                      aspectRatio: '0.78 / 1',
                      borderRadius: 3,
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      ) : destacados.length > 0 ? (
        <Box
          component="section"
          sx={{
            bgcolor: colorSuperficie,
            borderBottom: '1px solid #f1f5f9',
          }}
        >
          <Container
            maxWidth="xl"
            sx={{
              py: {
                xs: 5,
                md: 7,
              },
            }}
          >
            <Stack
              direction={{
                xs: 'column',
                sm: 'row',
              }}
              spacing={2}
              alignItems={{
                xs: 'flex-start',
                sm: 'flex-end',
              }}
              justifyContent="space-between"
              sx={{
                mb: 3,
              }}
            >
              <Box>
                <Typography
                  component="h2"
                  sx={{
                    color: colorTexto,
                    fontSize: {
                      xs: 27,
                      md: 34,
                    },
                    lineHeight: 1.1,
                    fontWeight: 900,
                    letterSpacing: '-0.035em',
                  }}
                >
                  {hayOfertasDestacadas
                    ? 'Ofertas y destacados'
                    : 'Productos destacados'}
                </Typography>

                <Typography
                  sx={{
                    mt: 0.8,
                    color: colorTextoSecundario,
                    lineHeight: 1.6,
                  }}
                >
                  {hayOfertasDestacadas
                    ? 'Aprovecha precios especiales y productos seleccionados para ti.'
                    : 'Una selección de productos que queremos recomendarte.'}
                </Typography>
              </Box>

              <Button
                component={RouterLink}
                to="/productos"
                sx={{
                  color: colorPrimario,
                  textTransform: 'none',
                  fontWeight: 800,
                  flexShrink: 0,
                }}
              >
                Ver todos los productos
              </Button>
            </Stack>

            <Grid container spacing={2.5}>
              {destacados.map((producto, index) => (
                <Grid
                  item
                  xs={6}
                  sm={6}
                  md={3}
                  key={
                    producto?.id ??
                    producto?.producto_id ??
                    producto?.sku ??
                    index
                  }
                >
                  <FeaturedProductCard
                    producto={producto}
                    colorPrimario={colorPrimario}
                    colorTexto={colorTexto}
                    colorTextoSecundario={
                      colorTextoSecundario
                    }
                    colorOferta={colorOferta}
                    colorExito={colorExito}
                    colorSuperficie={
                      colorSuperficie
                    }
                    colorFondo={colorFondo}
                    monedaDefault={moneda}
                    locale={locale}
                    mostrarStock={mostrarStock}
                    mostrarSku={mostrarSku}
                    mostrarOfertas={
                      mostrarOfertasInicio
                    }
                    mostrarPrecioNormalOferta={
                      mostrarPrecioNormalOferta
                    }
                    mostrarPorcentajeOferta={
                      mostrarPorcentajeOferta
                    }
                    mostrarAhorroOferta={
                      mostrarAhorroOferta
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      ) : null)}

      {/* =============================================================== */}
      {/* Catálogo                                                        */}
      {/* =============================================================== */}

      <Container
        maxWidth="xl"
        sx={{
          py: {
            xs: 5,
            md: 7,
          },
        }}
      >
        <Stack
          spacing={1.5}
          alignItems="center"
          textAlign="center"
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              color: colorTexto,
              letterSpacing: '-0.03em',
            }}
          >
            Explora nuestra tienda
          </Typography>

          <Typography
            sx={{
              maxWidth: 720,
              color: colorTextoSecundario,
              lineHeight: 1.7,
            }}
          >
            Encuentra productos disponibles, revisa sus detalles y agrega tus
            favoritos al carrito.
          </Typography>

          <Button
            component={RouterLink}
            to="/productos"
            variant="outlined"
            sx={{
              mt: 1,
              borderColor: colorPrimario,
              color: colorPrimario,
              borderRadius: 2,
              px: 3,
              textTransform: 'none',
              fontWeight: 800,
            }}
          >
            Ir al catálogo
          </Button>
        </Stack>
      </Container>
    </Box>
  );
}
